import { ReactElement } from 'react';
import { Typography } from 'antd';
import copy from 'copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from 'common/components/antd/Button/Button';
import { Space } from 'common/components/antd/Space/Space';
import { EnhancedModal, Body, Footer, Header } from 'common/components/EnhancedModal/EnhancedModal';
import { formatDateFromISO, titleCase } from 'common/utils/format.utils';
import { IReconciliationEntity } from 'domains/reconciliation/types/reconciliation.types';
import { SFooter } from './details.modal.style';
import { EQueryParams } from '../../enums/statistic.enums';

type TProps = {
  isOpen: boolean;
  onCancelDetailModalHandler: () => void;
  data: IReconciliationEntity;
};

const StatisticReconciliationDetailsModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelDetailModalHandler,
    data,
  } = props;

  const { t } = useTranslation();
  const { pathname } = useLocation();

  const copyLink = (): void => {
    const queryParams = `?${EQueryParams.Counterparty}=${data.creditor.guid}&${EQueryParams.Record}=${data.guid}`;
    const url = window.location.origin + pathname + queryParams;
    copy(url);
  };

  return (
    <EnhancedModal width={480} open={isOpen} onCancel={onCancelDetailModalHandler}>
      <Header>{t('reconciliation.statistic.modal.details.heading')}</Header>
      <Body>
        <Space
          size={16}
          direction="horizontal"
          style={{
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text>{t('reconciliation.statistic.modal.details.createdAt')}</Typography.Text>
          <Typography.Text style={{ fontSize: 16 }}>
            {data?.created_at ? formatDateFromISO(data.created_at) : ''}
          </Typography.Text>
        </Space>
        <Space
          size={16}
          direction="horizontal"
          style={{
            marginTop: 24,
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text>{t('reconciliation.statistic.modal.details.updatedAt')}</Typography.Text>
          <Typography.Text style={{ fontSize: 16 }}>
            {data?.updated_at ? formatDateFromISO(data.updated_at) : ''}
          </Typography.Text>
        </Space>
        <Space
          size={16}
          direction="horizontal"
          style={{
            marginTop: 24,
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text>{t('reconciliation.statistic.modal.details.debtor')}</Typography.Text>
          <Typography.Text style={{ fontSize: 16 }}>{data?.debtor?.name}</Typography.Text>
        </Space>
        <Space
          size={16}
          direction="horizontal"
          style={{
            marginTop: 24,
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text>{t('reconciliation.statistic.modal.details.creditor')}</Typography.Text>
          <Typography.Text style={{ fontSize: 16 }}>{data?.creditor?.name}</Typography.Text>
        </Space>
        <Space
          size={16}
          direction="horizontal"
          style={{
            marginTop: 24,
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text>{t('reconciliation.statistic.modal.details.amount')}</Typography.Text>
          <Typography.Text style={{ fontSize: 16 }}>
            {data?.calculations?.saldo} {data?.asset?.asset_code}
          </Typography.Text>
        </Space>
        <Space
          size={16}
          direction="horizontal"
          style={{
            marginTop: 24,
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text>{t('reconciliation.statistic.modal.details.amountProposed')}</Typography.Text>
          <Typography.Text style={{ fontSize: 16 }}>
            {data?.calculations?.saldo} {data?.asset?.asset_code}
          </Typography.Text>
        </Space>
        <Space
          size={16}
          direction="horizontal"
          style={{
            marginTop: 24,
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text>{t('reconciliation.statistic.modal.details.status')}</Typography.Text>
          <Typography.Text style={{ fontSize: 16 }}>
            {data?.status ? titleCase(data.status) : ''}
          </Typography.Text>
        </Space>
      </Body>
      <Footer showBorderTop={false}>
        <SFooter>
          <Button
            width="50%"
            size="large"
            style={{
              marginRight: 12,
            }}
            onClick={onCancelDetailModalHandler}
          >
            {t('reconciliation.statistic.modal.details.button.close')}
          </Button>
          <Button width="50%" size="large" onClick={copyLink} type="primary">
            {t('reconciliation.statistic.modal.details.button.copy')}
          </Button>
        </SFooter>
      </Footer>
    </EnhancedModal>
  );
};

export default StatisticReconciliationDetailsModal;
